import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Card, CardContent, Typography } from "@mui/material";
import { useLocales } from "../../../../locales";
import { useSnackbar } from "notistack";
import { useTheme } from "@mui/material/styles";
import {
    PrimaryLoadingButton
} from "../listing-details/listingButtons";
import { useAuthContext } from "src/hooks/useAuthContext";
import { useLazyQueryWithAuth, useMutationWithAuth, useQueryWithAuth } from "src/graphql/instances";
import {
    CLEAR_CART_ITEMS,
    CREATE_CART_ITEMS,
} from "src/graphql/mutations";
import getRoles from "../../../../utils/getRoles";
import { useQuery } from "@apollo/client";
import { RESERVATION_CONSTANTS } from "src/config/constants/reservation-constants";
import useResponsive from "../../../../hooks/useResponsive";
import { useNavigate } from "react-router-dom";
import { PATHS } from "../../../../routes/paths";
import {
    GET_CART,
    GET_EVENT,
} from "../../../../graphql/queries";
import DeleteForeignTicketsModal from "../listing-details/DeleteForeignTicketsModal";
import { useParams } from "react-router";
import EventImageLayout from "src/layouts/event";
import PropTypes from "prop-types";
import Iconify from "../../../../components/iconify";
import { getMonthRange, getMonthStartDateString, getWeekRange, getWeekStartDateString, isDayInPast } from "src/utils/dateUtils";
import ReactGA from "react-ga4";
import LoadingScreen from "src/components/loading-screen";
import ButtonDatePicker from "src/components/ButtonDatePicker";
import DateIcon from "src/assets/icons/DateIcon";
import ButtonArraySessionDatePicker from "src/components/ButtonArraySessionDatePicker";
import TicketIcon from "src/assets/icons/booking/TicketIcon";
import { EVENT_SESSION_AVAILABILITY, EVENT_SESSIONS, FIRST_AVAILABLE_SESSION, USER_EVENT_SESSIONS } from "src/graphql/queries/eventSessions";
import RecurringTicketShopCard from "./RecurringTicketShopCard";
import StickyFooter from "src/components/StickyFooter";
import { fCurrency } from "src/utils/formatNumber";
import { currencyMap } from "src/utils/currencies";
import { getShortDayMonthFromDate, getShortDayNameFromDate } from "src/utils/formatTime";
import { fCommission } from "src/utils/ticketPriceCalculatorFunctions";
import { SeatsChart } from "src/components/SeatsChart";

TicketsPage.propTypes = {
    eventId: PropTypes.string,
};

export default function TicketsPage({
    eventId,
}) {
    const { eventId: eventIdParam } = useParams();
    eventId = eventId || eventIdParam;

    const token = localStorage.getItem("accessToken");
    const { user } = useAuthContext();
    const navigate = useNavigate();
    const { data: cartData } = useQueryWithAuth(GET_CART, {
        fetchPolicy: "no-cache",
    });
    const [clearCartItems] = useMutationWithAuth(CLEAR_CART_ITEMS);
    const [createCartItems] = useMutationWithAuth(CREATE_CART_ITEMS);

    const { data: eventRes } = useQuery(GET_EVENT, {
        context: {
            headers: {
                "x-nightz-standard-behaviour": "true",
                "x-nightz-platform": "web",
                Authorization: token,
            },
        },
        variables: { eventId },
    });

    const [getEventSessions] = useLazyQueryWithAuth(USER_EVENT_SESSIONS, { fetchPolicy: "network-only" });
    const [getEventSessionAvailability] = useLazyQueryWithAuth(EVENT_SESSION_AVAILABILITY, { fetchPolicy: "network-only" });
    const [getFirstAvailableSession] = useLazyQueryWithAuth(FIRST_AVAILABLE_SESSION, { fetchPolicy: "network-only" })

    const [ticketQuantities, updateTicketQuantities] = useState(null);

    const eventData = useMemo(() => eventRes?.event, [eventRes]);
    const cart = useMemo(() => cartData?.cart, [cartData]);

    const [date, setDate] = useState(null);
    const [weekStart, setWeekStart] = useState(null);
    const [weekAvailability, setWeekAvailability] = useState([]);
    const [monthStart, setMonthStart] = useState(null);
    const [monthAvailability, setMonthAvailability] = useState([]);
    const [sessions, setSessions] = useState([]);

    const [activeSeatedSession, setActiveSeatedSession] = useState(null);
    const [showSeatsChart, setShowSeatsChart] = useState(false);
    const [chart, setChart] = useState(null);
    const [userSelectedAllSeats, setUserSelectedAllSeats] = useState(false);
    // ────────────────────────────────────────────────────────────────────────────

    const [hasOtherEventItemsInCart, setHasOtherEventItemsInCart] =
        useState(false);

    const [clearCartModalOpen, setClearCartModalOpen] = useState(false);
    const [isLoadingButton, setIsLoadingButton] = useState(false);
    const [isLoadingModalButton, setIsLoadingModalButton] = useState(false);
    const [isLoadingDate, setIsLoadingDate] = useState(false);

    const isMobile = useResponsive("down", "sm");
    const theme = useTheme();
    const { translate, currentLang: { value: language }, } = useLocales();
    const { enqueueSnackbar } = useSnackbar();

    const params = new URLSearchParams(window.location.search);
    const embedMode = params.get("embed") === "true";
    const isEmbedded = embedMode || window.self !== window.top;

    const loadingCheckAvailability = false;
    const totalCost = useMemo(() => ticketQuantities?.reduce(
        (acc, item) => acc + item.quantity * item.price,
        0
    ) || 0, [ticketQuantities])

    const seatingMapPricing = useMemo(() => {
        if (!activeSeatedSession) return null;
        return activeSeatedSession.ticketTypes.reduce((acc, ticket) => {
            if (ticket.isPublished) {
                acc.push({ category: ticket.name, price: ticket.price });
            }
            return acc;
        }, []);
    }, [activeSeatedSession]);

    const maxSelectableObjects = useMemo(() => {
        if (!ticketQuantities || !activeSeatedSession) return null;
        const sessionTickets = ticketQuantities.filter(
            (t) => t.sessionId === activeSeatedSession.id
        );
        return sessionTickets.map(({ name, quantity }) => ({
            category: name,
            quantity,
        }));
    }, [ticketQuantities, activeSeatedSession]);

    useEffect(() => {
        setChart(null);
        setUserSelectedAllSeats(false);
        setShowSeatsChart(false);
    }, [activeSeatedSession?.id]);

    useEffect(() => {
        if (cartData) {
            checkClearCartModalStatus();
        }
    }, [cartData]);

    const fetchSessions = async (date) => {

        const start = new Date(date);
        start.setHours(0, 0, 0, 0);
        const end = new Date(date);
        end.setHours(23, 59, 59, 999);

        try {
            const result = await getEventSessions({
                variables: {
                    eventId: eventId,
                    startDate: start,
                    endDate: end,
                },
            });
            setSessions(result.data.eventSessions);

        } catch (e) {
            console.log(e);
        }
    }

    const checkClearCartModalStatus = () => {
        const itemsToOtherEvent = cartData?.cart?.items?.filter(
            (it) =>
                (it.ticketType && it.ticketType?.eventId !== eventId) ||
                (it.bookingModule && it.bookingModule.event.general.id !== eventId)
        );

        setHasOtherEventItemsInCart(!!itemsToOtherEvent?.length);
    };



    const getHeader = (title, icon, error) => (
        <Box display="flex" flexDirection="column" sx={{ mt: 2.7, mb: 0.9 }}>
            <Box display="flex" alignItems="center" gap={1}>
                {!!icon &&
                    (typeof icon === "string" ? (
                        <Iconify icon={icon} />
                    ) : (
                        icon
                    ))}
                <Typography variant="subtitle1" sx={{ fontFamily: "Visby" }}>{title}</Typography>
            </Box>
        </Box>
    );

    const checkCompanyRole = () => {
        if (getRoles().Company.includes(user.role)) {
            enqueueSnackbar(translate("bookings.errorNoPermissionCompany"), {
                variant: "error",
                autoHideDuration: 5000,
            });
        }
    }

    const handleStepBack = () => {
        navigate(-1)
    }

    const handleChangeDate = (newDate) => {
        setDate(new Date(newDate))
    }

    const handleChangeMonth = async (month) => {
        try {
            const { startDate, endDate } = getMonthRange(month);

            const availabilityRes = await getEventSessionAvailability({
                variables: {
                    eventId: eventId,
                    startDate: startDate,
                    endDate: endDate
                }
            })
            setMonthAvailability(availabilityRes?.data?.eventSessionAvailability);
        } catch (e) {
            console.log(e)
        }
    }

    const handleChangeWeek = async (weekStart) => {
        try {
            const { startDate, endDate } = getWeekRange(weekStart);

            const availabilityRes = await getEventSessionAvailability({
                variables: {
                    eventId: eventId,
                    startDate: startDate,
                    endDate: endDate
                }
            })
            setWeekAvailability(availabilityRes?.data?.eventSessionAvailability);
        } catch (e) {
            console.log(e)
        }
    }

    const initializeAvailability = async () => {
        try {
            const res = await getFirstAvailableSession({
                variables: {
                    eventId: eventId,
                    startDate: new Date()
                }
            });
            const firstAvailable = res?.data?.firstAvaiableSession;
            if (firstAvailable) {
                setDate(new Date(firstAvailable));
            } else {
                setDate(new Date());
            }
        } catch {
            enqueueSnackbar("Could not fetch availability", {
                variant: "error",
                autoHideDuration: 5000,
            });
        }
    }

    useEffect(() => {
        initializeAvailability();
    }, [])

    useEffect(() => {
        if (!date) return;
        setWeekStart(getWeekStartDateString(date));
        setMonthStart(getMonthStartDateString(date));
        fetchSessions(date);

    }, [date])

    useEffect(() => {
        if (!weekStart) return;
        handleChangeWeek(weekStart)

    }, [weekStart])

    useEffect(() => {
        if (!monthStart) return;
        handleChangeMonth(monthStart)

    }, [monthStart])

    const shouldDisableDate = (date) => {
        if (isDayInPast(date)) return true;
        if (monthAvailability.find((d) => d.date === date.format(RESERVATION_CONSTANTS.YEAR_FORMAT))) return false;
        return true;
    };

    // ── Buy handler ──────────────────────────────────────────────────────────────
    const doBuy = useCallback(
        async (skipCheckingOtherCartItems = false) => {
            if (isLoadingButton) return;
            setIsLoadingButton(true);
            try {
                if (!skipCheckingOtherCartItems && hasOtherEventItemsInCart) {
                    setClearCartModalOpen(true);
                    setIsLoadingButton(false);
                    return;
                }

                await clearCartItems();

                const selectedSeats = showSeatsChart && chart
                    ? await chart.listSelectedObjects()
                    : null;

                const completeCartItems = ticketQuantities?.map((ticket) => ({
                    ...ticket,
                    ...(selectedSeats && ticket.sessionId === activeSeatedSession?.id
                        ? {
                            seatsHoldToken: chart.holdToken,
                            seatIds: selectedSeats
                                .filter((seat) => seat.category.label === ticket.name)
                                .map((seat) => seat.seatId),
                        }
                        : {}),
                }));

                if (ticketQuantities?.length) {
                    await createCartItems({
                        variables: {
                            cartItems: completeCartItems.map((it) => ({
                                id: it.id,
                                quantity: it.quantity,
                                seatIds: it.seatIds,
                                seatsHoldToken: it.seatsHoldToken,
                                eventSessionId: it.sessionId,
                            })),
                        },
                    });
                }

                if (showSeatsChart) {
                    setShowSeatsChart(false);
                }

                sendGAEvent(completeCartItems);
                navigate(PATHS.listings.checkout + (isEmbedded ? '?embed=true' : ''));
                setIsLoadingButton(false);
            } catch (err) {
                console.error(err);
                setIsLoadingButton(false);
                enqueueSnackbar(translate("productPage.genericError"), {
                    variant: "error",
                    autoHideDuration: 5000,
                });
            }
        },
        [
            ticketQuantities,
            chart,
            showSeatsChart,
            activeSeatedSession,
            hasOtherEventItemsInCart,
        ]
    );

    const sendGAEvent = (completeCartItems) => {
        const selected_tickets = completeCartItems?.map((it) => ({
            item_id: it.id,
            item_name: it.name,
            item_list_id: eventData?.general?.id,
            item_list_name: eventData?.general?.id,
            price: it.price,
            quantity: it.quantity
        })) || [];
        ReactGA.event("add_to_cart", {
            currency: eventData?.general?.currency,
            value: fCurrency(totalCost),
            item_list_id: eventData?.general?.id,
            item_list_name: eventData?.general?.name,
            items: [...selected_tickets]
        });
        // fbq("track", "AddToCart", {
        //     currency: eventData?.general?.currency,
        //     value: fCurrency(totalCost),
        //     content_name: eventData?.general?.name,
        //     content_ids: selected_tickets.map((it) => it.item_id ?? it.id),
        //     contents: selected_tickets.map((it) => ({
        //         id: it.item_id ?? it.id,
        //         quantity: it.quantity ?? 1,
        //         item_price: it.price ?? it.item_price,
        //     })),
        // });
    }


    if (!eventData) {
        return <LoadingScreen />;
    }
    if (showSeatsChart) {
        return (
            <>
                <EventImageLayout
                    title={`Tickets | ${eventData?.general?.name}`}
                    image={eventData?.general?.images[0]}
                    fields={eventData?.general?.fields}
                    useFullHeight={true}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-start",
                        }}
                    >
                        <Iconify
                            icon="eva:chevron-left-fill"
                            width={40}
                            sx={{ zIndex: 2, cursor: "pointer" }}
                            onClick={() => setShowSeatsChart(false)}
                        />
                        <Typography
                            variant="h5"
                            color={theme.palette.text.primary}
                            sx={{ zIndex: 2, mx: "auto" }}
                        >
                            {translate("bookingWizard.seatsTitle")}
                        </Typography>
                    </Box>

                    <SeatsChart
                        eventChartKey={activeSeatedSession.seatsIoEventKey}
                        maxSelectableSeats={maxSelectableObjects}
                        seatingMapPricing={seatingMapPricing}
                        onChartRendered={(renderedChart) => {
                            setChart(renderedChart);
                        }}
                        setUserSelectedAllSeats={setUserSelectedAllSeats}
                    />

                    <StickyFooter>
                        <Box sx={{ textAlign: "center" }}>
                            <PrimaryLoadingButton
                                size="large"
                                variant="contained"
                                loading={isLoadingButton}
                                disabled={
                                    !ticketQuantities?.length ||
                                    !userSelectedAllSeats ||
                                    !chart
                                }
                                sx={{
                                    width: isMobile ? "calc(100% - 48px)" : "200px",
                                    margin: isMobile ? "16px 24px 24px 24px" : "auto",
                                }}
                                onClick={() => doBuy()}
                            >
                                {translate("productPage.buyNow")} · {totalCost ? `${fCurrency(fCommission(totalCost, eventData?.tickets?.comission, eventData?.general?.commissionPaidByUser))} ${currencyMap[eventData?.general?.currency]}` : translate('eventManager.free')}
                            </PrimaryLoadingButton>
                        </Box>
                    </StickyFooter>
                </EventImageLayout>

                <DeleteForeignTicketsModal
                    modalOpen={clearCartModalOpen}
                    setModalOpen={setClearCartModalOpen}
                    confirmDeleteCallback={() => doBuy(true)}
                    isLoading={isLoadingModalButton}
                />
            </>
        );
    }

    return (
        <EventImageLayout
            title={`Tickets | ${eventData?.general?.name}`}
            image={eventData?.general?.images[0]}
            fields={eventData?.general?.fields}
            useFullHeight={true}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",

                }}
            >
                <Iconify icon="eva:chevron-left-fill" width={40} sx={{ zIndex: 2, cursor: "pointer" }} onClick={handleStepBack} />
                <Typography
                    variant="h5"
                    color={theme.palette.text.primary}
                    sx={{ zIndex: 2, mx: "auto" }}
                >
                    {translate('tickets.configuration')}
                </Typography>
            </Box>

            <Card
                sx={{
                    mt: 2,
                    backgroundColor: "transparent",
                    marginBottom: 1,
                    boxShadow: 0,
                }}
            >
                <CardContent
                    sx={{
                        "&.MuiCardContent-root": { padding: isMobile ? 0 : 3 },
                        minHeight: 600
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            width: "100%",
                        }}
                    >
                        {getHeader(
                            translate("bookings.date"),
                            <DateIcon />,
                        )}
                    </Box>
                    {(date && weekStart) ? (<Box display="flex" alignItems="center" borderRadius="12px" padding={1} paddingX={isMobile ? 0 : 1} gap={1}>
                        <ButtonArraySessionDatePicker
                            changeDate={handleChangeDate}
                            weekStart={weekStart}
                            setWeekStart={setWeekStart}
                            date={date}
                            weekAvailability={weekAvailability}
                            isLoading={false}
                        />
                        {!isMobile && (<ButtonDatePicker
                            changeDate={handleChangeDate}
                            date={date}
                            isSmall={false}
                            onMonthChange={(month) => {
                                setMonthStart(getMonthStartDateString(month));
                            }}

                            disabled={false}
                            shouldDisableDate={shouldDisableDate}
                            slotProps={{
                                DatePickerToolbar: { style: { display: "none" } },
                                nextIconButton: {
                                    style: {
                                        pointerEvents: loadingCheckAvailability ? "none" : "auto",
                                    },
                                },
                                previousIconButton: {
                                    style: {
                                        pointerEvents: loadingCheckAvailability ? "none" : "auto",
                                    },
                                },
                            }}
                            disableFuture={loadingCheckAvailability}
                        />)}
                    </Box>) : (<Typography variant="subtitle1">{translate('tickets.loadingDates')}</Typography>)}

                    {isMobile && (
                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                            <ButtonDatePicker
                                changeDate={handleChangeDate}
                                date={date}
                                isSmall={false}
                                isText={true}
                                onMonthChange={(month) => {
                                    setMonthStart(getMonthStartDateString(month));
                                }}

                                disabled={false}
                                shouldDisableDate={shouldDisableDate}
                                slotProps={{
                                    DatePickerToolbar: { style: { display: "none" } },
                                    nextIconButton: {
                                        style: {
                                            pointerEvents: loadingCheckAvailability ? "none" : "auto",
                                        },
                                    },
                                    previousIconButton: {
                                        style: {
                                            pointerEvents: loadingCheckAvailability ? "none" : "auto",
                                        },
                                    },
                                }}
                                disableFuture={loadingCheckAvailability}
                            />
                        </Box>

                    )}

                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            width: "100%",
                        }}
                    >
                        {getHeader(
                            `${translate("bookingWizard.tickets")}` + (date ? ` ${translate("eventManager.sessions.for")} ${getShortDayNameFromDate(date, language)}, ${getShortDayMonthFromDate(date, language)}` : ''),
                            <TicketIcon />,
                        )}
                    </Box>
                    {sessions?.length ? (<RecurringTicketShopCard
                        sessions={sessions}
                        cart={cart}
                        event={eventData}
                        initialTicketQuantities={ticketQuantities}
                        updateTicketQuantities={updateTicketQuantities}
                        onSeatedSessionChange={setActiveSeatedSession}
                    />) : (<Typography variant="subtitle1">{translate('tickets.noTickets')}</Typography>)}
                </CardContent>
            </Card>

            <StickyFooter>
                <Box
                    sx={{
                        textAlign: "center",
                    }}
                >
                    <PrimaryLoadingButton
                        size="large"
                        variant="contained"
                        loading={isLoadingButton}
                        disabled={!ticketQuantities?.length}
                        sx={{
                            width: isMobile ? "calc(100% - 48px)" : "200px",
                            margin: isMobile ? "16px 24px 24px 24px" : "auto",
                        }}
                        onClick={() => {
                            if (activeSeatedSession) {
                                setShowSeatsChart(true);
                            } else {
                                doBuy();
                            }
                        }}
                    >
                        {translate("productPage.buyNow")} · {totalCost ? `${fCurrency(fCommission(totalCost, eventData?.tickets?.comission, eventData?.general?.commissionPaidByUser))} ${currencyMap[eventData?.general?.currency]}` : translate('eventManager.free')}
                    </PrimaryLoadingButton>
                </Box>
            </StickyFooter>


            <DeleteForeignTicketsModal
                modalOpen={clearCartModalOpen}
                setModalOpen={setClearCartModalOpen}
                confirmDeleteCallback={() => doBuy(true)}
                isLoading={isLoadingModalButton}
            />
        </EventImageLayout>
    );
}